import { faClock } from "@fortawesome/pro-duotone-svg-icons"
import { faChevronLeft, faChevronRight, faTimes } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Checkbox, Select, Slider, Typography } from "antd"
import { useEffect } from "react"
import { useAccountSetup } from "./AccountSetupContext"
import { useTenantWorkDaySettingsDataQuery, useUpdateTenantWorkDaySettingsMutation } from "../../domain/tenantWorkDaySettings/tenantWorkDaySettingsQueries"
import './AccountSetupModal.css'

const { Title, Text } = Typography

export type Props = {
  onBack: () => void
  onExit: () => void
}

// Time options for dropdowns
const timeOptions = [
  '5:00 AM', '5:30 AM', '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM',
  '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
  '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
  '8:00 PM', '8:30 PM', '9:00 PM'
].map(time => ({ label: time, value: time }))

// Convert "8:00 AM" to "08:00:00"
const convertTo24Hour = (time12h: string): string => {
  const [time, modifier] = time12h.split(' ')
  const parts = time.split(':').map(Number)
  let hours = parts[0]
  const minutes = parts[1]

  if (modifier === 'PM' && hours !== 12) {
    hours += 12
  } else if (modifier === 'AM' && hours === 12) {
    hours = 0
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
}

// Convert "08:00:00" or ISO datetime to "8:00 AM"
const convertTo12Hour = (time24h: string): string => {
  // Handle ISO datetime format (e.g., "2024-01-01T08:00:00+00:00")
  let timePart = time24h
  if (time24h.includes('T')) {
    timePart = time24h.split('T')[1].split('+')[0].split('Z')[0]
  }

  const [hoursStr, minutesStr] = timePart.split(':')
  let hours = parseInt(hoursStr, 10)
  const minutes = parseInt(minutesStr, 10)

  const modifier = hours >= 12 ? 'PM' : 'AM'
  if (hours === 0) {
    hours = 12
  } else if (hours > 12) {
    hours -= 12
  }

  return `${hours}:${minutes.toString().padStart(2, '0')} ${modifier}`
}

const AccountSetupWorkHoursStep = ({ onBack, onExit }: Props) => {
  const { state, dispatch } = useAccountSetup()
  const { workHours } = state

  // API hooks
  const { data: existingSettings } = useTenantWorkDaySettingsDataQuery()
  const updateWorkDaySettings = useUpdateTenantWorkDaySettingsMutation()

  const isSaving = updateWorkDaySettings.isLoading

  // Prefill from existing data (only if reducer state has defaults)
  useEffect(() => {
    const isUsingDefaults = workHours.startTime === '8:00 AM' && workHours.endTime === '5:00 PM'

    if (isUsingDefaults && existingSettings) {
      const settings = Array.isArray(existingSettings) ? existingSettings[0] : existingSettings
      if (settings?.dayStartTime) {
        const startTime12h = convertTo12Hour(settings.dayStartTime)
        const endTime12h = convertTo12Hour(settings.dayEndTime)

        // Only update if the converted time is in our dropdown options
        if (timeOptions.some(opt => opt.value === startTime12h)) {
          dispatch({ type: 'UPDATE_WORK_HOURS', field: 'startTime', value: startTime12h })
        }
        if (timeOptions.some(opt => opt.value === endTime12h)) {
          dispatch({ type: 'UPDATE_WORK_HOURS', field: 'endTime', value: endTime12h })
        }
        dispatch({ type: 'UPDATE_WORK_HOURS', field: 'lunchBreakMinutes', value: settings.estimatedBreakTimeMinutes || 30 })
        dispatch({ type: 'UPDATE_WORK_HOURS', field: 'autoApplyLunchBreaks', value: settings.hasBreakTime })
      }
    }
  }, [existingSettings])

  const handleNext = async () => {
    await updateWorkDaySettings.mutateAsync({
      dayStartTime: convertTo24Hour(workHours.startTime),
      dayEndTime: convertTo24Hour(workHours.endTime),
      hasBreakTime: workHours.autoApplyLunchBreaks,
      estimatedBreakTimeMinutes: workHours.lunchBreakMinutes
    })
    dispatch({ type: 'NEXT_STEP' })
  }

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '37.5%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faClock} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            When Do You Work?
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Set up your crew's work hours so time tracking runs smoothly.
          </Text>
        </div>

        <div className="account-setup-form">
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Start Time</Text>
              <Select
                size="large"
                style={{ width: '100%' }}
                value={workHours.startTime}
                options={timeOptions}
                onChange={(value) => dispatch({ type: 'UPDATE_WORK_HOURS', field: 'startTime', value })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>End Time</Text>
              <Select
                size="large"
                style={{ width: '100%' }}
                value={workHours.endTime}
                options={timeOptions}
                onChange={(value) => dispatch({ type: 'UPDATE_WORK_HOURS', field: 'endTime', value })}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Estimated Lunch/Break Time</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Slider
                style={{ flex: 1 }}
                min={0}
                max={120}
                step={15}
                value={workHours.lunchBreakMinutes}
                onChange={(value) => dispatch({ type: 'UPDATE_WORK_HOURS', field: 'lunchBreakMinutes', value })}
                tooltip={{ formatter: (value) => `${value} min` }}
              />
              <Text style={{ minWidth: 60 }}>{workHours.lunchBreakMinutes} Minutes</Text>
            </div>
          </div>

          <Checkbox
            checked={workHours.autoApplyLunchBreaks}
            onChange={(e) => dispatch({ type: 'UPDATE_WORK_HOURS', field: 'autoApplyLunchBreaks', value: e.target.checked })}
          >
            Automatically apply lunch breaks to crew time
          </Checkbox>

          <div className="account-setup-form-buttons" style={{ marginTop: 48 }}>
            <Button size="large" onClick={onBack} disabled={isSaving}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              loading={isSaving}
              disabled={isSaving}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </div>

      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-3.png"
          alt="Work hours setup"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <div className="account-setup-modal-testimonial-wrapper">
          <div className="account-setup-modal-testimonial">
            <Text className="account-setup-testimonial-quote">
              "I just sold our first major job with SynkedUP and I know I'm making money on it. Super pumped!"
            </Text>
          </div>
          <div className="account-setup-testimonial-author">
            <Text strong style={{ color: 'white' }}>Logan Conklin</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: 14 }}>
              Canals Landscape Services
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSetupWorkHoursStep
